import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { User, LoginCredentials, ChangePasswordData, LoginResponse } from '../types';
import apiService from '../services/api';

interface AuthContextType {
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  login: (credentials: LoginCredentials) => Promise<void>;
  logout: () => void;
  changePassword: (data: ChangePasswordData) => Promise<void>;
  error: string | null;
  clearError: () => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const initAuth = async () => {
      try {
        const token = localStorage.getItem('accessToken');
        const email = localStorage.getItem('userEmail');
        const userId = localStorage.getItem('userId');
        const tenantId = localStorage.getItem('tenantId');

        if (token && email && userId) {
          // Validate token
          const validation = await apiService.validateToken(token);
          if (validation.valid) {
            let restored: User = { id: userId, email, tenantId: tenantId || undefined };
            try {
              const saved = localStorage.getItem('user');
              if (saved) {
                const parsed = JSON.parse(saved);
                if (parsed?._id) {
                  restored = { id: parsed._id, email: parsed.email || email, tenantId: parsed.tenantId || tenantId || undefined };
                }
              }
            } catch {}
            setUser(restored);
            setIsAuthenticated(true);
          } else {
            // Token is invalid, clear auth
            apiService.clearAuth();
          }
        }
      } catch (error) {
        console.error('Auth initialization error:', error);
        apiService.clearAuth();
      } finally {
        setIsLoading(false);
      }
    };

    initAuth();
  }, []);

  // Redirect unauthenticated users to login, except when already on /login
  useEffect(() => {
    if (!isLoading) {
      if (!isAuthenticated && location.pathname !== '/login') {
        navigate('/login', { replace: true, state: { from: location } });
      }
      if (isAuthenticated && location.pathname === '/login') {
        navigate('/dashboard', { replace: true });
      }
    }
  }, [isAuthenticated, isLoading, location, navigate]);

  const login = async (credentials: LoginCredentials) => {
    try {
      setIsLoading(true);
      setError(null);
      
      const res: LoginResponse = await apiService.login(credentials);
      const userFromServer = res.user;
      const userData = {
        id: userFromServer._id,
        email: userFromServer.email,
        tenantId: userFromServer.tenantId
      } as User;
      apiService.setAuth({ accessToken: res.accessToken, refreshToken: res.refreshToken }, userData);
      setUser(userData);
      setIsAuthenticated(true);
    } catch (error: any) {
      const errorMessage = error.response?.data?.message || error.message || 'Login failed';
      setError(errorMessage);
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  const logout = () => {
    apiService.clearAuth();
    setUser(null);
    setIsAuthenticated(false);
    setError(null);
    navigate('/login', { replace: true });
  };

  const changePassword = async (data: ChangePasswordData) => {
    try {
      setIsLoading(true);
      setError(null);
      
      if (data.newPassword !== data.confirmPassword) {
        throw new Error('New passwords do not match');
      }
      
      await apiService.changePassword(data);
    } catch (error: any) {
      const errorMessage = error.response?.data?.message || error.message || 'Password change failed';
      setError(errorMessage);
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  const clearError = () => {
    setError(null);
  };

  const value: AuthContextType = {
    user,
    isAuthenticated,
    isLoading,
    login,
    logout,
    changePassword,
    error,
    clearError
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
